// Import 3rd-party dependencies
import { Injectable } from '@angular/core';
import { BehaviorSubject, combineLatest, interval, Observable, of } from 'rxjs';
import { catchError, map, shareReplay, startWith, switchMap } from 'rxjs/operators';

// Import project-specific dependencies
import { ApprovalCentralNotificationService } from '@app/services/notifications/approval-central/approval-central-notification.service';
import { DxcTimeNotificationService } from '@app/services/notifications/dxctime/dxctime-notification.service';
import { WorkdayNotificationService } from '@app/services/notifications/workday/workday-notification.service';
import { PeopleService } from '@app/services/people/people.service';
import { UserProfileService } from '@app/services/user-profile/user-profile.service';
import { UamNotificationService, IUamNotification } from './uam/uam-notification.service';
import { NOTIFICATION_SERVICE_CONSTS } from '@app/config/constants';
import { DxchubNotificationService } from './dxchub/dxchub-notification.service';

export interface INotificationInfo {
  announcementID?: string;
  from?: string;
  title: string;
  link: string;
  message: string;
  count: number;
  actionNeeded: boolean;
  due?: Date;
  origObj?: unknown;
}

@Injectable({
  providedIn: 'root'
})
export class NotificationsService {
  consts = NOTIFICATION_SERVICE_CONSTS;

  private readonly coreId$ = this.peopleService.currentUser$.pipe(
    map((user) => user?.employeeId || ''),
    shareReplay()
  );
  private readonly email$ = this.peopleService.currentUser$.pipe(
    map((user) => user?.mail || ''),
    shareReplay()
  );
  private readonly employeeId$ = this.coreId$.pipe(
    switchMap((coreId) => this.profileService.getDdProfile(coreId)),
    map((ddProfile) => ddProfile?.personnel_number || ''),
    shareReplay()
  );

  private approvalCentralNotifs$ = interval(this.consts.POLL_INTERVAL).pipe(
    startWith(0),
    switchMap(() => this.coreId$),
    switchMap((coreId) => this.approvalCentralNotificationService.getPendingApprovals(coreId)),
    catchError(() => of(null as INotificationInfo)),
    shareReplay()
  );
  private dxcTimeNotifs$ = interval(this.consts.POLL_INTERVAL).pipe(
    startWith(0),
    switchMap(() => this.employeeId$),
    switchMap((employeeId) => this.dxcTimeNotificationService.getNotifications(employeeId)),
    catchError(() => of(null as INotificationInfo)),
    shareReplay()
  );
  private uamNotifs$ = interval(this.consts.POLL_INTERVAL).pipe(
    startWith(0),
    switchMap(() => this.email$),
    switchMap((email) => this.uamNotificationService.getNotifications(email)),
    catchError(() => of(null as INotificationInfo)),
    shareReplay()
  );
  private workdayNotifs$ = interval(this.consts.POLL_INTERVAL).pipe(
    startWith(0),
    switchMap(() => this.employeeId$),
    switchMap((employeeId) => this.workdayNotificationService.getNotifications(employeeId)),
    catchError(() => of(null as INotificationInfo)),
    shareReplay()
  );

  private dxcHubNotifs$ = interval(this.consts.POLL_INTERVAL).pipe(
    startWith(0),
    switchMap(() => this.dxchubNotificationService.getNotifications()),
    catchError(() => of(null as INotificationInfo[])),
    shareReplay()
  );
  /* UAM NOTIFICATION START*/
  private uamSources: Observable<INotificationInfo>[] = [
    this.uamNotifs$
   ];
   uamNotification$: Observable<INotificationInfo[]> = combineLatest(this.uamSources)
   .pipe(
     map((uam) => uam.filter((item) => item && item.count > 0))
   );

 /* UAM NOTIFICATION END */
  // TASKS - START
  private taskSources: Observable<INotificationInfo>[] = [
    this.approvalCentralNotifs$,
    this.dxcTimeNotifs$
    // TODO: [TH-1346] Enable once available
    // this.uamRenewals$
  ];
  tasks$: Observable<INotificationInfo[]> = combineLatest([...this.taskSources, ...this.uamSources]).pipe(
    map((nolist) => {
        return this.getFlatArray(nolist) as INotificationInfo[];
      })
    )
    .pipe(map((notifications) => notifications.filter((item) => item && item.count > 0)));
  // TASKS - END

  // NOTIFICATIONS - START
  private notificationSources: Observable<INotificationInfo>[] = [
    this.workdayNotifs$
    // TODO: [TH-1346] Enable once available
    // this.uamApprovals$
  ];
  
  notifications$: Observable<INotificationInfo[]> = combineLatest([this.dxcHubNotifs$, ...this.notificationSources])
    .pipe(
      map((nolist) => {
        return this.getFlatArray(nolist) as INotificationInfo[];
      })
    )
    .pipe(map((notifications) => notifications.filter((item) => item && item.count > 0)));
  // NOTIFICATIONS - END

  // ALERTS - START
  private alertSources: Observable<INotificationInfo>[] = [
    // TODO: Add data sources here once alerts are available
  ];
  alerts$: Observable<INotificationInfo[]> = combineLatest(this.alertSources).pipe(
    map((alerts) => alerts.filter((item) => item && item.count > 0))
  );
  // ALERTS - END

  // ALL NOTIFICATIONS - START
  allNotifs$: Observable<INotificationInfo[]> = combineLatest([
    ...new Set([...this.taskSources, this.dxcHubNotifs$, ...this.notificationSources, ...this.alertSources, ...this.uamSources])
  ])
    .pipe(
      map((nolist) => {
        return this.getFlatArray(nolist) as INotificationInfo[];
      })
    )
    .pipe(map((allNotifs) => allNotifs.filter((item) => item && item.count > 0)));
  // ALL NOTIFICATIONS - END

  constructor(
    private approvalCentralNotificationService: ApprovalCentralNotificationService,
    private dxcTimeNotificationService: DxcTimeNotificationService,
    private workdayNotificationService: WorkdayNotificationService,
    // TODO: [TH-1346] Enable once available
    private uamNotificationService: UamNotificationService,
    private dxchubNotificationService: DxchubNotificationService,
    private peopleService: PeopleService,
    private profileService: UserProfileService
  ) {}

  private getFlatArray(list: (INotificationInfo[] | INotificationInfo)[]): INotificationInfo[] {
    return list.reduce((pre: any, item: any) => {
      return pre.concat(item);
    }, []);
  }

  private notifListRefreshedSource = new BehaviorSubject('');
  private notifListRefreshed$ = this.notifListRefreshedSource.asObservable();

  public filterOutDisabledItems(observable: Observable<INotificationInfo[]>) {
    return combineLatest([observable, this.notifListRefreshed$]).pipe(
      map(([nolist]) => {
        return nolist.filter((list) => this.getDisabledNotifList().indexOf(list.announcementID) === -1);
      })
    );
  }

  private getDisabledNotifList(): string[] {
    const disabledNotifList = JSON.parse(localStorage.getItem('DisabledNotifList'));
    return disabledNotifList || [];
  }

  public refreshNoifList() {
    this.notifListRefreshedSource.next('');
  }
}
